/**
 * OEM Home Dashboard — shared types.
 *
 * `TimeRange` is the global time-range filter that scopes the whole Home page.
 * The page (`homeDashboardPage.tsx`) owns the `range` state (default `'7d'`) and
 * passes it down to the map (task c) and the activity card (task d); both derive
 * their query window from it. Keep this in one place so the page, the
 * `<TimeRangeSelector/>`, and the `useHomeActivity*` hooks agree on the union.
 */
export type TimeRange = 'today' | '7d' | '30d' | '90d' | '1y' | 'all';

/** Default range on first render (matches design + backend `resolveRange`). */
export const DEFAULT_TIME_RANGE: TimeRange = '7d';

/**
 * Props every Home slot component receives from the page. Task c
 * (`<ActivityMap/>`) and task d (`<TimeRangeSelector/>`, `<CardStrip>` /
 * `<ActivityCard/>`) plug into these slots and read/lift `range` through here.
 */
export interface HomeRangeProps {
  /** The active global time range. */
  range: TimeRange;
}

/** Props for the controlled `<TimeRangeSelector/>` (task d wires the body). */
export interface TimeRangeSelectorProps extends HomeRangeProps {
  /** Called when the user picks a different range; lifts state to the page. */
  onChange: (range: TimeRange) => void;
}

/**
 * Props for a bottom-strip card (Phase 2). All cards take `range`; the three
 * map-plottable cards (Activity / Assets / Routines) also take `selected` +
 * `onToggleSelect` to drive the map's plotting mode. Products / Campaigns /
 * Customers ignore them (not plottable).
 */
export interface HomeCardProps extends HomeRangeProps {
  /** Whether this card's plotting mode is active on the map. */
  selected?: boolean;
  /** Toggles this card's plotting mode (lifts to the page). */
  onToggleSelect?: () => void;
  /**
   * The status/segment currently filtering the map for THIS card's entity, or
   * null. Used to highlight the selected chart item. The value matches the map
   * point's `status` (asset category, activity bucket 'urgent'|'pending'|
   * 'solved', or routine name).
   */
  activeStatus?: string | null;
  /**
   * Lifts a clicked chart item to the page: selects this card's plotting mode
   * and filters the map to `status` (clicking the active one again clears it).
   */
  onSelectStatus?: (status: string) => void;
}
